const NeighborhoodPicker = {
    init() {
        if (this.cacheDom()) {
            this.observePicker();
            this.updatePointerLimits();
            this.bindEvents();
            this.pointAtActive();
            this.syncDetailPlacement();
        }
    },

    cacheDom() {
        this.root = document.querySelector('[data-neighborhood-picker]');
        if (!this.root) return false;

        this.pickerItems = this.root.querySelector('[data-neighborhood-picker-items]');
        this.detail = this.root.querySelector('[data-neighborhood-picker-detail]');
        this.dial = this.root.querySelector('[data-neighborhood-picker-dial]');
        this.dialButton = this.root.querySelector('[data-neighborhood-picker-dial-button]');
        this.pointer = this.root.querySelector('[data-neighborhood-picker-pointer]');
        this.panels = this.root.querySelectorAll('.neighborhood-picker__detail-item');
        if (!this.pickerItems) return false;

        const items = this.root.querySelectorAll('[data-neighborhood-picker-item]');
        this.items = Array.from(items).reduce((acc, item) => {
            const id = item.dataset.neighborhoodPickerItem;
            const detail = this.root.querySelector(`#${id}`);
            acc[id] = {
                item,
                detail,
            };
            return acc;
        }, {});

        const styles = getComputedStyle(this.root);
        this.pickerWidth = parseFloat(styles.getPropertyValue('--picker-width')) || 670;
        this.scaleDampen = parseFloat(styles.getPropertyValue('--scale-dampen'));
        if (Number.isNaN(this.scaleDampen)) {
            this.scaleDampen = 1;
        }
        this.dialScaleDampen = parseFloat(styles.getPropertyValue('--dial-scale-dampen'));
        if (Number.isNaN(this.dialScaleDampen)) {
            this.dialScaleDampen = 1;
        }

        this.pointerTrack = styles.getPropertyValue('--pointer-track').trim() || 'map';
        const homeAngle = parseFloat(styles.getPropertyValue('--pointer-home'));
        this.homeAngle = Number.isFinite(homeAngle) ? homeAngle : 45;
        this.homeId = this.dialButton?.dataset.neighborhoodPickerHome || null;
        const pointerPad = parseFloat(styles.getPropertyValue('--pointer-pad'));
        this.pointerPad = Number.isFinite(pointerPad) ? pointerPad : 12;
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerLeave = this.handlePointerLeave.bind(this);
        this.accordionMq = window.matchMedia('(max-width: 900.98px)');

        const expandedPin = Object.keys(this.items).find((id) => (
            this.items[id].item.getAttribute('aria-expanded') === 'true'
        ));
        const visiblePanel = this.root.querySelector('.neighborhood-picker__detail-item:not([hidden])');
        this.activeId = expandedPin || visiblePanel?.id || this.homeId;
        this.isHovering = false;
        this.pointerAngle = this.homeAngle;
        this.pointerStart = null;
        this.pointerSpan = 360;
        this.placementToken = 0;
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        return true;
    },

    observePicker() {
        this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));
        this.resizeObserver.observe(this.pickerItems);
    },

    handleResize(entries) {
        for (const entry of entries) {
            const raw = entry.contentRect.width / this.pickerWidth;
            entry.target.style.setProperty('--scale', this.lerpScale(raw, this.scaleDampen));
            entry.target.style.setProperty('--dial-scale', this.lerpScale(raw, this.dialScaleDampen));
        }

        this.updatePointerLimits();

        if (!this.isHovering) {
            this.pointAtActive();
        }
    },

    lerpScale(raw, dampen) {
        return 1 + (raw - 1) * dampen;
    },

    bindEvents() {
        Object.values(this.items).forEach(({ item }) => {
            item.addEventListener('click', this.handleClick.bind(this));
        });

        if (this.dialButton && this.homeId) {
            this.dialButton.addEventListener('click', this.handleDialClick.bind(this));
        }

        this.accordionMq.addEventListener('change', this.handleAccordionChange.bind(this));

        if (this.pointer && !this.prefersReducedMotion) {
            this.bindPointerTracking();
        }
    },

    bindPointerTracking() {
        if (this.pointerTrack === 'document') {
            document.addEventListener('pointermove', this.onPointerMove);
            document.documentElement.addEventListener('mouseleave', this.onPointerLeave);
            return;
        }

        const surface = this.pointerTrack === 'module'
            ? this.root
            : this.pickerItems;

        surface.addEventListener('pointermove', this.onPointerMove);
        surface.addEventListener('pointerleave', this.onPointerLeave);
    },

    handleClick(e) {
        const id = e.currentTarget.dataset.neighborhoodPickerItem;
        if (id === this.activeId) {
            if (this.isAccordion()) {
                this.scrollToActiveItem();
            }
            return;
        }

        this.show(id);
    },

    handleDialClick() {
        this.show(this.homeId);
        this.pointAtActive();
    },

    handlePointerMove(e) {
        if (e.target.closest('[data-neighborhood-picker-dial-button]')) {
            return;
        }

        this.isHovering = true;
        this.setPointerAngle(this.angleTo(e.clientX, e.clientY));
    },

    handlePointerLeave() {
        this.isHovering = false;
        this.pointAtActive();
    },

    show(id) {
        this.activeId = id;

        Object.values(this.items).forEach(({ item }) => {
            item.setAttribute(
                'aria-expanded',
                String(item.dataset.neighborhoodPickerItem === id)
            );
        });

        if (this.dialButton) {
            this.dialButton.setAttribute('aria-pressed', String(id === this.homeId));
        }

        if (this.isAccordion()) {
            this.syncDetailPlacement({ scroll: true, animate: true });
        } else {
            this.swapPanels(id);
            this.restoreDetail();
        }

        if (!this.isHovering) {
            this.pointAtActive();
        }
    },

    swapPanels(id = this.activeId) {
        this.panels.forEach((panel) => {
            panel.toggleAttribute('hidden', panel.id !== id);
        });
    },

    isAccordion() {
        return this.accordionMq.matches;
    },

    handleAccordionChange() {
        this.syncDetailPlacement();

        if (!this.isAccordion()) {
            this.swapPanels();
            this.updatePointerLimits();
            if (!this.isHovering) {
                this.pointAtActive();
            }
        }
    },

    syncDetailPlacement({ scroll = false, animate = false } = {}) {
        if (!this.detail) return;

        if (this.isAccordion()) {
            this.placeDetailAfterActive({ animate, scroll });
            return;
        }

        this.restoreDetail();
    },

    placeDetailAfterActive({ animate = false, scroll = false } = {}) {
        const active = this.items[this.activeId];
        if (!active?.item) return;

        const token = ++this.placementToken;
        const reveal = () => {
            if (token !== this.placementToken) return;

            this.swapPanels();
            active.item.after(this.detail);

            if (scroll) {
                this.scrollToActiveItem();
            }

            if (!animate || this.prefersReducedMotion) {
                this.detail.classList.add('is-expanded');
                return;
            }

            this.detail.offsetHeight;
            this.detail.classList.add('is-expanded');
        };

        if (animate && !this.prefersReducedMotion && this.detail.classList.contains('is-expanded')) {
            this.collapseDetail(token).then(reveal);
            return;
        }

        reveal();
    },

    collapseDetail(token) {
        return new Promise((resolve) => {
            if (!this.detail.classList.contains('is-expanded')) {
                resolve();
                return;
            }

            let settled = false;
            const finish = () => {
                if (settled || token !== this.placementToken) return;
                settled = true;
                this.detail.removeEventListener('transitionend', onEnd);
                window.clearTimeout(this.collapseTimer);
                resolve();
            };
            const onEnd = (e) => {
                if (e.target === this.detail) {
                    finish();
                }
            };

            this.detail.addEventListener('transitionend', onEnd);
            this.collapseTimer = window.setTimeout(finish, 400);
            this.detail.classList.remove('is-expanded');
        });
    },

    scrollToActiveItem() {
        const item = this.items[this.activeId]?.item;
        if (!item) return;

        item.scrollIntoView({
            behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
            block: 'start',
        });
    },

    restoreDetail() {
        this.detail.classList.add('is-expanded');

        if (!this.pickerItems || this.pickerItems.nextElementSibling === this.detail) {
            return;
        }

        this.pickerItems.after(this.detail);
    },

    isHomeActive() {
        return Boolean(this.homeId) && this.activeId === this.homeId;
    },

    pointAtActive() {
        if (!this.pointer) return;

        if (this.isHomeActive() || !this.items[this.activeId]) {
            this.setPointerAngle(this.homeAngle, { clamp: false });
            return;
        }

        const point = this.itemPoint(this.items[this.activeId].item);
        this.setPointerAngle(this.angleTo(point.x, point.y));
    },

    angleTo(x, y) {
        const rect = this.dial.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        return Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90;
    },

    normalizeAngle(angle) {
        return ((angle % 360) + 360) % 360;
    },

    unwrapNear(angle, current) {
        return angle + 360 * Math.round((current - angle) / 360);
    },

    itemPoint(item) {
        const rect = item.getBoundingClientRect();
        return {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
        };
    },

    itemAngle(item) {
        const point = this.itemPoint(item);
        return this.normalizeAngle(this.angleTo(point.x, point.y));
    },

    updatePointerLimits() {
        const dialRect = this.dial?.getBoundingClientRect();
        if (!dialRect || dialRect.width === 0 || !this.items) {
            this.pointerStart = null;
            this.pointerSpan = 360;
            return;
        }

        const angles = Object.values(this.items)
            .filter(({ item }) => !item.classList.contains('is-yesler'))
            .map(({ item }) => this.itemAngle(item))
            .filter((angle) => Number.isFinite(angle))
            .sort((a, b) => a - b);

        if (angles.length === 0) {
            this.pointerStart = null;
            this.pointerSpan = 360;
            return;
        }

        if (angles.length === 1) {
            this.pointerStart = angles[0];
            this.pointerSpan = 0;
            return;
        }

        let maxGap = -1;
        let maxGapIndex = 0;

        for (let i = 0; i < angles.length; i += 1) {
            const current = angles[i];
            const next = i === angles.length - 1 ? angles[0] + 360 : angles[i + 1];
            const gap = next - current;
            if (gap > maxGap) {
                maxGap = gap;
                maxGapIndex = i;
            }
        }

        const startIndex = maxGapIndex === angles.length - 1 ? 0 : maxGapIndex + 1;
        const rawStart = angles[startIndex];
        const rawEnd = angles[maxGapIndex];
        const rawSpan = this.normalizeAngle(rawEnd - rawStart) || 360;
        const pad = Math.min(this.pointerPad, Math.max(0, (360 - rawSpan) / 2));

        this.pointerStart = this.normalizeAngle(rawStart - pad);
        this.pointerSpan = Math.min(360, rawSpan + pad * 2);
    },

    clampToAllowed(angle) {
        if (this.pointerStart == null || this.pointerSpan >= 360) {
            return angle;
        }

        const offset = this.normalizeAngle(angle - this.pointerStart);
        if (offset <= this.pointerSpan) {
            return this.pointerStart + offset;
        }

        const toEnd = offset - this.pointerSpan;
        const toStart = 360 - offset;
        return toEnd <= toStart
            ? this.pointerStart + this.pointerSpan
            : this.pointerStart;
    },

    shortestPathTo(target) {
        const current = this.pointerAngle;
        const delta = Math.atan2(
            Math.sin((target - current) * Math.PI / 180),
            Math.cos((target - current) * Math.PI / 180)
        ) * 180 / Math.PI;

        this.pointerAngle = current + delta;
        this.pointer.style.setProperty('--pointer-angle', `${this.pointerAngle}deg`);
    },

    setPointerAngle(target, { clamp = true } = {}) {
        if (!clamp) {
            this.shortestPathTo(target);
            return;
        }

        const allowed = this.clampToAllowed(target);
        const current = this.pointerAngle;

        if (this.pointerStart == null || this.pointerSpan >= 360) {
            this.shortestPathTo(allowed);
            return;
        }

        const start = this.pointerStart;
        const span = this.pointerSpan;
        const targetOffset = Math.min(span, Math.max(0, this.normalizeAngle(allowed - start)));
        const targetUnwrapped = start + targetOffset;
        const visualOffset = this.normalizeAngle(current - start);

        // Parked at 45° (Yesler) sits outside the pin sweep; take the short hop
        // onto the nearest pin instead of interpolating through the empty arc.
        if (visualOffset > span) {
            this.shortestPathTo(allowed);
            return;
        }

        this.pointerAngle = this.unwrapNear(start + visualOffset, current)
            + (targetUnwrapped - (start + visualOffset));
        this.pointer.style.setProperty('--pointer-angle', `${this.pointerAngle}deg`);
    },
};

export default NeighborhoodPicker;
